import { Instagram, Facebook, Linkedin, Youtube } from 'lucide-react';
import { SOCIAL_PROFILES } from '../data/socialLinks';

const ICON_FOR = {
  instagram: Instagram,
  facebook: Facebook,
  linkedin: Linkedin,
  youtube: Youtube,
};

/**
 * SocialLinks — brand-aware SOCIAL block. Shows the label of the division
 * so visitors know which brand's channels they're about to follow.
 *
 *   <SocialLinks brand="smg" />
 *   <SocialLinks brand="sports" />
 *   <SocialLinks brand="ralli" />
 *
 * Any channel with `url: null` renders as a disabled tile — the moment
 * you add a real URL in /app/frontend/src/data/socialLinks.js, the tile
 * becomes a live anchor opening in a new tab.
 */
export default function SocialLinks({ brand = 'smg', accent = '#FF3D2E' }) {
  const profile = SOCIAL_PROFILES[brand] || SOCIAL_PROFILES.smg;
  return (
    <div data-testid={`social-${brand}`}>
      <p
        className="text-[10px] tracking-[0.3em] text-white/45 mb-1"
        style={{ color: accent === '#FF3D2E' ? 'rgba(255,255,255,0.45)' : accent }}
      >
        SOCIAL
      </p>
      <p className="text-[11px] tracking-[0.3em] text-white/70 mb-3">
        {profile.label}
      </p>
      <div className="flex gap-2.5">
        {profile.channels.map((ch) => {
          const Icon = ICON_FOR[ch.name];
          if (!Icon) return null;
          const disabled = !ch.url;
          const Wrapper = disabled ? 'span' : 'a';
          const labelName = ch.name.charAt(0).toUpperCase() + ch.name.slice(1);
          const props = disabled
            ? {
                'aria-disabled': 'true',
                'aria-label': `${profile.label} on ${labelName} — coming soon`,
                role: 'img',
                title: `${ch.handle} — coming soon`,
              }
            : { href: ch.url, target: '_blank', rel: 'noopener noreferrer', 'aria-label': `${profile.label} on ${labelName}` };
          return (
            <Wrapper
              key={ch.name}
              {...props}
              className={`w-9 h-9 border flex items-center justify-center transition-colors ${
                disabled
                  ? 'border-white/8 text-white/25 cursor-default'
                  : 'border-white/15 text-white/70 hover:border-white hover:text-white'
              }`}
              data-testid={`social-${brand}-${ch.name}`}
            >
              <Icon className="w-4 h-4" />
            </Wrapper>
          );
        })}
      </div>
    </div>
  );
}
